/**
 * MatterConversationsPanel — the first consumer of the Conversation Engine.
 *
 * Shows a matter's communication the way a barrister files it: by professional objective
 * (Instruction, Conference, Settlement, …), NOT by email thread. Every producer — email,
 * portal, internal note, conference — is already unified server-side into purpose-typed
 * conversations with per-message privilege; this panel just renders them.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChatsCircle, CaretRight, Lock, Users, MagnifyingGlass } from '@phosphor-icons/react';
import {
  fetchMatterConversations, conversationsToChronology, searchConversationsClient,
  PURPOSE_LABELS, CHANNEL_LABELS,
  type MatterConversation, type ConversationPrivilege,
} from '@/lib/conversationsApi';
import { conversationToWorkItem } from '@/lib/conversationRouting';
import { routeWork, ACTIONABLE_DISPOSITIONS, DISPOSITION_LABELS } from '@/lib/routingEngine';

const PRIVILEGE_BADGE: Record<ConversationPrivilege, { label: string; cls: string }> = {
  privileged:  { label: 'Privileged',  cls: 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border-red-200 dark:border-red-500/30' },
  operational: { label: 'Operational', cls: 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30' },
  external:    { label: 'External',    cls: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30' },
};

function relTime(iso: string): string {
  const h = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (h < 1) return 'just now';
  if (h < 24) return `${Math.floor(h)}h ago`;
  if (h < 24 * 30) return `${Math.floor(h / 24)}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function MatterConversationsPanel({ matterId }: { matterId: string }) {
  const [view, setView] = useState<'objective' | 'chronological'>('objective');
  const [query, setQuery] = useState('');
  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ['matter-conversations', matterId],
    queryFn: () => fetchMatterConversations(matterId),
  });

  if (isLoading) {
    return <div className="py-12 text-center text-sm text-muted-foreground">Loading conversations…</div>;
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
        <ChatsCircle className="h-9 w-9 text-muted-foreground/60" />
        <div className="text-sm font-medium text-foreground">No conversations yet</div>
        <div className="max-w-sm text-xs text-muted-foreground">
          Email, portal messages, notes and conferences on this matter will appear here, grouped by
          what they&apos;re about — instruction, conference, settlement, and so on.
        </div>
      </div>
    );
  }

  // Most-recently-active first.
  const ordered = [...conversations].sort((a, b) => lastAt(b) - lastAt(a));
  const chronology = conversationsToChronology(conversations);
  const q = query.trim();
  const hits = q ? searchConversationsClient(conversations, query) : null;

  return (
    <div className="space-y-2">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div className="relative min-w-[220px] flex-1">
          <MagnifyingGlass className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search all channels — email, portal, notes, conference…"
            className="w-full rounded-md border border-border bg-card py-1.5 pl-8 pr-3 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-600/40"
          />
        </div>
        {!q && (
          <div className="inline-flex rounded-md border border-border p-0.5 text-xs">
            {(['objective', 'chronological'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`rounded px-2.5 py-1 font-medium transition ${
                  view === v ? 'bg-brand-50 text-brand-700 dark:text-brand-300' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {v === 'objective' ? 'By objective' : 'Chronological'}
              </button>
            ))}
          </div>
        )}
      </div>

      {hits
        ? (
          hits.length === 0
            ? <div className="py-8 text-center text-xs text-muted-foreground">No messages match “{q}” on this matter.</div>
            : (
              <div className="space-y-1.5">
                <div className="text-[11px] text-muted-foreground">{hits.length} result{hits.length !== 1 ? 's' : ''} across every channel</div>
                {hits.map(({ message: m, purpose }) => <EntryRow key={m.id} message={m} purpose={purpose} />)}
              </div>
            )
        )
        : view === 'objective'
          ? ordered.map((c) => <ConversationRow key={c.id} conversation={c} />)
          : <div className="space-y-1.5">{chronology.map(({ message: m, purpose }) => <EntryRow key={m.id} message={m} purpose={purpose} />)}</div>}
    </div>
  );
}

// A single message row tagged with its objective — shared by the chronological view and search.
function EntryRow({ message: m, purpose }: { message: MatterConversation['messages'][number]; purpose: MatterConversation['purpose'] }) {
  const badge = PRIVILEGE_BADGE[m.privilege];
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2">
      <div className="mb-0.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span className="rounded-full bg-muted px-1.5 py-0.5 font-medium text-foreground/80">{PURPOSE_LABELS[purpose]}</span>
        <span className="font-medium text-foreground/70">{CHANNEL_LABELS[m.channel]}</span>
        <span className={`rounded border px-1.5 py-0.5 font-medium ${badge.cls}`}>{badge.label}</span>
        <span>· {relTime(m.sentAt)}</span>
      </div>
      {m.subject && <div className="text-xs font-medium text-foreground">{m.subject}</div>}
      <div className="line-clamp-2 text-xs text-muted-foreground">{m.body}</div>
    </div>
  );
}

function ConversationRow({ conversation: c }: { conversation: MatterConversation }) {
  const [open, setOpen] = useState(false);
  const last = c.messages[c.messages.length - 1];
  const channels = [...new Set(c.messages.map((m) => m.channel))];
  const hasPrivileged = c.messages.some((m) => m.privilege === 'privileged');
  // The Routing Engine, consuming the conversation directly (step 7).
  const disposition = routeWork(conversationToWorkItem(c)).disposition;
  const needsAction = ACTIONABLE_DISPOSITIONS.includes(disposition);

  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-3.5 py-2.5 text-left hover:bg-muted/30"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">{PURPOSE_LABELS[c.purpose]}</span>
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
              {c.messages.length}
            </span>
            {hasPrivileged && <Lock className="h-3.5 w-3.5 text-red-500" weight="fill" />}
            {needsAction && (
              <span className="rounded-full border border-brand-200 bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700 dark:text-brand-300">
                {DISPOSITION_LABELS[disposition]}
              </span>
            )}
            {c.status === 'closed' && (
              <span className="rounded-full border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">closed</span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {c.participants.length}</span>
            <span>· {channels.map((ch) => CHANNEL_LABELS[ch]).join(' · ')}</span>
            {last && <span className="truncate">· {relTime(last.sentAt)}</span>}
          </div>
        </div>
        <CaretRight className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="space-y-1.5 border-t border-border px-3.5 py-3">
          {c.messages.map((m) => {
            const badge = PRIVILEGE_BADGE[m.privilege];
            return (
              <div key={m.id} className="rounded-md border border-border/60 bg-muted/20 px-2.5 py-2">
                <div className="mb-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="font-medium text-foreground/70">{CHANNEL_LABELS[m.channel]}</span>
                  <span className={`rounded border px-1.5 py-0.5 font-medium ${badge.cls}`}>{badge.label}</span>
                  <span>· {relTime(m.sentAt)}</span>
                </div>
                {m.subject && <div className="text-xs font-medium text-foreground">{m.subject}</div>}
                <div className="line-clamp-2 text-xs text-muted-foreground">{m.body}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function lastAt(c: MatterConversation): number {
  const last = c.messages[c.messages.length - 1];
  return last ? new Date(last.sentAt).getTime() : new Date(c.createdAt).getTime();
}
